<script lang="ts">
  import { getSwapBonus } from '~/algorithms/gameLogic';
  import type { Coord, Match } from '~/types';
  import GameOver, { gameOverModal } from './GameOver.svelte';
  import { shuffle, shuffleBoard } from '~/algorithms/shuffle';
  import { seededRng } from '~/algorithms/rng';
  import { animationSpeed } from '~/animations/utils.svelte';
  import { starInAnimation, starOutAnimation } from '~/animations.svelte';
  import { delay } from '~/animations/utils.svelte';
  import GameBoard, { collapsing, highlightLetter } from '~/board/Board.svelte';
  import { setStars, clearStars } from '~/board/Stars.svelte';
  import { gamesPlayed } from '~/store/gameState.svelte';
  import WordContainer from '~/components/WordContainer.svelte';
  import StatsBar from '~/Stats.svelte';
  import { saveLocalLeaderboard } from '~/leaderboard/leaderboard.svelte';
  import Gauge from '~/gauge/classic/Gauge.svelte';
  import Score from '~/pills/Score.svelte';
  import Shuffle from '~/pills/Shuffle.svelte';
  import {
    getEntry,
    saveClassicAnalytics,
    saveClassicSnapshot,
  } from '~/lib/mutations.svelte';
  import { onMount, tick, untrack } from 'svelte';
  import { useIdleCallback } from '~/lib/idle.svelte';
  import { beforeNavigate } from '$app/navigation';
  import Search from '~/pills/Search.svelte';
  import { isPremium } from '~/lib/queries.svelte';
  import { lastSyncedAt, syncEnabled } from './SyncModal.svelte';
  import { nudgePremium } from '~/modals/PremiumModal.svelte';
  import { navHistory } from '~/lib/nav.svelte';
  import { fade } from 'svelte/transition';
  import {
    growAnimation,
    sendWordAnimation,
    crossing,
  } from '~/animations/crossfade.svelte';
  import { flipAnimation, shuffling } from '~/animations/flip.svelte';
  import DrawerButton from '~/components/buttons/DrawerButton.svelte';
  import FooterSpacer from '~/components/FooterSpacer.svelte';
  import { tutorialViewed } from '~/tutorial/state.svelte';
  import { dictionary, dictionaryLoaded } from '~/algorithms/dictionary.svelte';
  import { getGame } from '~/store/GameContext.svelte';
  import { ClassicGame } from '~/store/classic.svelte';
  import { getDimensions } from '~/utils/DimensionsContext.svelte';
  import { shareModal } from '~/share/ShareModal.svelte';
  import TutorialButton from '~/tutorial/TutorialButton.svelte';
  import SyncIndicator from './SyncIndicator.svelte';
  import { playerName } from '~/auth/utils.svelte';
  import HorizontalAlphabeticalScrollBar from '~/components/HorizontalAlphabeticalScrollBar.svelte';
  import { settings } from '~/modals/Settings.svelte';

  const handleLetterScroll = (letter: string) => {
    // Drive the same board highlight the keyboard uses.
    highlightLetter.current = letter.toLowerCase();
  };

  // Letters currently present on the board, so the scroll bar can grey out
  // letters that have zero occurrences.
  const boardLetters = $derived.by(() => {
    const present = new Set<string>();
    for (const col of game.board) {
      for (const tile of col) {
        if (tile?.letter) present.add(tile.letter.toUpperCase());
      }
    }
    return [...present];
  });

  const dims = getDimensions();
  const premium = isPremium();

  let containerWidth = $state(0);
  let containerHeight = $state(0);

  const game = $derived(getGame<ClassicGame>().current);

  const saveClassic = saveClassicAnalytics();
  const snapshot = saveClassicSnapshot(lastSyncedAt);

  let lastSnapshotTurns = -1;
  const maybeSaveSnapshot = () => {
    if (!game.lost && game.turns === lastSnapshotTurns) return;
    lastSnapshotTurns = game.turns;
    if (premium.data && syncEnabled.current) {
      snapshot.mutate({ game });
    }
  };

  useIdleCallback(maybeSaveSnapshot);

  const showStats = false;

  onMount(async () => {
    if (game.turns > 0 && !game.lost) {
      game.disabled = true;
      await Promise.all([dictionaryLoaded.current, await delay(500)]);
      // if game was left in incomplete clear state
      await handleScore();
      await game.save();
      game.disabled = false;
    } else if (game.lost) {
      game.disabled = true;
      if (navHistory.current.length === 0) {
        await delay(100);
        await gameOverModal.push();
      }
    } else {
      game.disabled = false;
    }
  });

  onMount(() => {
    window.addEventListener('pagehide', maybeSaveSnapshot);
    return () => window.removeEventListener('pagehide', maybeSaveSnapshot);
  });

  beforeNavigate(({ type, shallow }) => {
    if (shallow) return;

    if (type === 'leave') {
      maybeSaveSnapshot();
    }
  });

  $effect(() => {
    if (game.turns % 5 === 0) {
      untrack(() => {
        maybeSaveSnapshot();
      });
    }
  })

  const handleEndTurn = async (chain: number = 0, shuffle: boolean = false) => {
    const penalty = 3;
    // broken streak
    if (!shuffle && chain === 0) {
      game.streak -= penalty;
      // drop down a level
      if (game.streak < 0) {
        game.streakLevel = game.streakLevel - 1 || 1;
        game.streak += game.streakLevel * game.streakInterval;
      }
    }

    if (game.swaps <= 0) {
      game.disabled = true;
      await delay(animationSpeed.ms * 2);
      game.lost = true;
      game.marquee = '😞 GAME OVER';
    } else {
      // re-enable input faster
      game.disabled = false;
      await delay(animationSpeed.ms * 3);
      game.marquee = undefined;
    }
    // persist stats and leaderboard data
    if (game.lost) {
      await handleGameOver();
    } else {
      await game.save();
    }
    return game.lost;
  };

  const handleGameOver = async () => {
    if (game.endedAt) return;
    game.endedAt = Date.now();
    game.duration = Math.round((game.endedAt - game.startedAt!) / 1000);
    game.disabled = true;
    gamesPlayed.current++;
    await game.save();
    maybeSaveSnapshot();
    await gameOverModal.push();
    if (gamesPlayed.current === 2) {
      await shareModal.push();
    } else if (!premium.data) {
      await nudgePremium(gamesPlayed.current);
    }
    // TODO this isn't saving
    game.saveStats();
    // TODO migrate local leaderboard to indexeddb?
    saveLocalLeaderboard(await getEntry(game, playerName.current));
    saveClassic.mutate(game);
    // TODO surface count somewhere in UI
  };

  // chain is incremented on subsequent recursive calls of handleScore
  const handleScore = async (chain = 0, shuffle = false) => {
    const { words, intersections } = await dictionary.findWords(
      $state.snapshot(game.board),
      game.mode,
    );

    // recursive base case
    if (!words.length) {
      await game.collapseBoard();
      return await handleEndTurn(chain, shuffle);
    } else {
      tutorialViewed.current = true;
    }

    game.highlightWords(words);
    game.intersections = intersections;

    for (const [i, word] of Object.entries(words)) {
      await game.appendWord(word);
      game.latestWord = word;
      // clear letters, flying them (and their shadows) to the word pile
      await crossing.launch(word, intersections, () => game.removeLetters(word));

      const wordChain = game.words - game.prevWordPointer;
      game.setMarqueeText(word, wordChain);

      const swapBonus = getSwapBonus(word, chain);
      const animateStars = async () => {
        if (swapBonus === 0) return;
        await growAnimation.settled();
        setStars({
          stars: swapBonus,
          coords: [
            [word.coords[0][0], word.coords[0][1]],
            [
              word.coords[word.coords.length - 1][0],
              word.coords[word.coords.length - 1][1],
            ],
          ],
        });

        // reveal stars before new letters fall in
        await starInAnimation.settled();
        clearStars();
        await starOutAnimation.settled();
      };

      // allow $animations to play
      score(word, chain + +i, swapBonus);
      await Promise.all([game.save(), delay(50)]);
      await Promise.all([sendWordAnimation.settled(), animateStars()]);
    }

    // fill empty spaces
    collapsing.current = game.board.map(
      (col) => game.dims[1] - col.filter((t) => t).length,
    );
    await tick();
    await game.collapseBoard();

    await delay(animationSpeed.ms);
    collapsing.current = [];
    await tick();
    // increment chain only if no other words on board
    await handleScore(chain + words.length);
    await game.save();

    // game is not lost
    return false;
  };

  const score = (word: Match, chain: number, swapBonus: number) => {
    game.streak++;
    const threshold = game.streakLevel * game.streakInterval;
    if (game.streak >= threshold) {
      // TODO improve this animation
      const newStreak = game.streak - threshold;
      game.streak = threshold;
      setTimeout(() => {
        game.streak = newStreak;
        game.streakLevel++;
      }, 750);
    }

    // update swaps and shuffles
    if (chain > 0 && word.intersectingIds.length) {
      game.shuffles++;
    }
    if (game.isFullLength(word)) {
      game.shuffles++;
    }
    game.swaps += swapBonus;
    game.score += word.score;
  };

  const handleShuffle = async () => {
    if (!game.disabled) {
      game.disabled = true;
      game.shuffles--;
      shuffling.current = true;
      await tick();
      // TODO ?
      // clearSelection();
      const coords = Array.from({ length: game.board.length }).flatMap((_, i) =>
        Array.from({ length: game.board[0].length }).map(
          (_, j): Coord => [i, j],
        ),
      );

      const rng = seededRng(game.id, game.turns + game.score);
      const shuffled = shuffle([...coords], rng);
      game.board = shuffleBoard(game.board, coords, shuffled);
      // pause for animation
      await flipAnimation.settled();
      await tick();
      // await delay(1000);

      await game.save();

      shuffling.current = false;
      await handleScore(0, true);
      await game.save();
      await game.appendTurn(shuffled);
    }
  };
</script>

<div
  class="top-container"
  style="--board-width: min({dims.board.width}px, 100%)"
  bind:clientWidth={containerWidth}
  bind:clientHeight={containerHeight}
>
  <DrawerButton />
  <Score score={game.score} />
  {#if !game.lost}
    <SyncIndicator snapshot={snapshot} />
  {/if}
  <Gauge />
  {#if !game.lost}
    <div class="bottom-right-controls" transition:fade>
      <Shuffle shuffles={game.shuffles} onShuffle={handleShuffle} />
    </div>
    <div class="bottom-left-controls">
      <TutorialButton iconOnly />

      <Search
        containerWidth={containerWidth}
        containerHeight={containerHeight}
      />
    </div>
  {/if}
</div>

<GameBoard
  bind:disabled={game.disabled}
  handleScore={handleScore}
  handleEndTurn={handleEndTurn}
/>

{#if settings.alphaScrollBar.current && !game.lost}
  <div class="alpha-bar" style="--board-width: min({dims.board.width}px, 100%)">
    <HorizontalAlphabeticalScrollBar
      validLetters={boardLetters}
      onletterchange={handleLetterScroll}
    />
  </div>
{/if}
{#if showStats}
  <StatsBar />
{/if}
<FooterSpacer />
{#if game.lost}
  {#await game.getPage('score', 0) then words}
    <GameOver words={words} />
  {/await}
{:else if game.words}
  <WordContainer />
{/if}

<style lang="scss">
  @use '~/theme/include-media' as *;

  .alpha-bar {
    @media (min-aspect-ratio: 1 / 1) {
      width: var(--board-width);
    }
    @media (max-aspect-ratio: 1 / 1) {
      width: 100%;
    }
  }
  .bottom-right-controls {
    position: absolute;
    bottom: 0;
    right: 0;
  }
  .bottom-left-controls {
    position: absolute;
    bottom: 0;
    left: 0;
    display: flex;
    gap: 4px;
  }
  .top-container {
    position: relative;
    @media (min-aspect-ratio: 1 / 1) {
      width: var(--board-width);
    }
    @media (max-aspect-ratio: 1 / 1) {
      width: 100%;
    }
    @include media('<=phone') {
      font-size: 0.8rem;
    }
    @include media('>phone', '<=tablet') {
      font-size: 1rem;
    }
    @include media('>tablet') {
      font-size: 1.2rem;
    }
    :global(.pill) {
      position: absolute;
      bottom: 0;
      left: 0;
    }
  }
</style>
